/**
 * Identificador deste navegador para a amarração da sessão (header `X-Client-Id`).
 *
 * O login manda o valor, e o servidor grava o hash dele na sessão. A partir daí toda
 * requisição autenticada precisa trazer o mesmo identificador: um token copiado e
 * usado em outro lugar sem ele é recusado, e com um valor diferente a sessão é
 * derrubada (ver docs/features/session-security-and-xss.md, Fase 2).
 *
 * É separado do `DeviceToken` do 2FA de propósito: aquele dispensa o código de
 * verificação e sobrevive ao logout; se ele também amarrasse a sessão, roubá-lo
 * valeria mais.
 */

export const CLIENT_ID_KEY = 'ClientId';
export const CLIENT_ID_HEADER = 'X-Client-Id';

/** Sorteado no máximo uma vez por carregamento quando o localStorage não responde. */
let cachedClientId: string | null = null;

function randomClientId(): string {
	const bytes = new Uint8Array(16);
	window.crypto.getRandomValues(bytes);
	return 'c1_' + Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * A requisição vai para a NOSSA API? (URL relativa, ou absoluta com a origem do `baseUrl`).
 * O `X-Client-Id` só pode ir para ela: um header não-simples força preflight de CORS, e
 * serviços de terceiros (ViaCEP, Memed, S3) recusam o header e bloqueiam a chamada.
 */
export function isOwnApiRequest(url: string | undefined, baseURL: string | undefined, ownOrigin: string): boolean {
	const full = url ?? '';
	if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(full)) {
		// Relativa: vale a do `baseURL` da instância, ou a própria página.
		return !baseURL || baseURL.startsWith('/') || sameOrigin(baseURL, ownOrigin);
	}
	return sameOrigin(full, ownOrigin);
}

/** Compara a origem inteira — `startsWith` aceitaria `https://app.com.outro-site.com`. */
function sameOrigin(a: string, b: string): boolean {
	try {
		return new URL(a).origin === new URL(b).origin;
	} catch {
		return false;
	}
}

/** Identificador deste navegador, criado na primeira chamada e reaproveitado sempre. */
export function getClientId(): string {
	if (cachedClientId) return cachedClientId;

	try {
		const stored = window.localStorage.getItem(CLIENT_ID_KEY);
		if (stored) {
			cachedClientId = stored;
			return stored;
		}

		cachedClientId = randomClientId();
		window.localStorage.setItem(CLIENT_ID_KEY, cachedClientId);
		return cachedClientId;
	} catch {
		cachedClientId = cachedClientId ?? randomClientId();
		return cachedClientId;
	}
}
